import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { CheckCircleOutline as AnsweredIcon } from '@mui/icons-material';
import { answersApi } from '../../services/api';
import { CommonQuestion } from '../../types';
import { errorMessage } from '../../utils/errors';

interface Props {
  /** Keys of the questions the "Application answers" tab shows a row for; null while unknown. */
  libraryKeys: Set<string> | null;
  /** Jump to that question's row on the "Application answers" tab. */
  onAnswerStandard: (questionKey: string) => void;
  /** Open the custom-answer editor with this prompt filled in. */
  onAnswerCustom: (prompt: string) => void;
}

const formatCount = (value: number): string => value.toLocaleString('en-US');

/** "Asked by 42 jobs (30 require it)". */
function askedBy(row: CommonQuestion): string {
  const jobs = `Asked by ${formatCount(row.jobs)} ${row.jobs === 1 ? 'job' : 'jobs'}`;
  if (!row.required_jobs) return jobs;
  return `${jobs} (${row.required_jobs === row.jobs ? 'all' : formatCount(row.required_jobs)} require it)`;
}

/**
 * The questions employers on the job board ask most, unanswered first.
 * Answering them here makes more listings "ready to apply".
 */
const CommonQuestionsTab: React.FC<Props> = ({ libraryKeys, onAnswerStandard, onAnswerCustom }) => {
  const [rows, setRows] = useState<CommonQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await answersApi.common());
    } catch (e) {
      setError(errorMessage(e, 'Could not load the questions employers ask.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const answered = useMemo(() => rows.filter((row) => row.answered).length, [rows]);

  const action = (row: CommonQuestion): React.ReactNode => {
    if (row.answered) {
      return <Chip icon={<AnsweredIcon />} label="Answered" size="small" color="success" variant="outlined" />;
    }
    if (row.question_key === null) {
      return (
        <Button size="small" variant="outlined" onClick={() => onAnswerCustom(row.label)}>
          Add custom answer
        </Button>
      );
    }
    if (libraryKeys && !libraryKeys.has(row.question_key)) {
      // Phone, LinkedIn and the like come from the profile, not from here.
      return (
        <Button size="small" variant="outlined" component={RouterLink} to="/profile">
          Fill in on profile
        </Button>
      );
    }
    const key = row.question_key;
    return (
      <Button size="small" variant="contained" onClick={() => onAnswerStandard(key)}>
        Answer
      </Button>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What application forms on the job board ask most often. Every question you answer here
        makes more listings ready to apply to.
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        !error && (
          <Typography variant="body2" color="text.secondary">
            No application forms have been read yet. Check back once the job board has refreshed.
          </Typography>
        )
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {answered} of {rows.length} answered.
          </Typography>
          <Box>
            {rows.map((row) => (
              <Stack
                key={row.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                    {row.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {askedBy(row)}
                    {row.kind === 'custom' ? ' | Employer wording' : ''}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0 }}>{action(row)}</Box>
              </Stack>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default CommonQuestionsTab;
